import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { toast } from 'sonner';
import { useDeleteOffer } from '../api';

interface TrashConfirmDialogProps {
  isOpen: boolean;
  offerId: string;
  onClose: () => void;
}

export function TrashConfirmDialog({ isOpen, offerId, onClose }: TrashConfirmDialogProps) {
  if (!isOpen) {
    return null;
  }

  return <TrashConfirmDialogContent offerId={offerId} onClose={onClose} />;
}

function TrashConfirmDialogContent({ offerId, onClose }: Omit<TrashConfirmDialogProps, 'isOpen'>) {
  const { t } = useTranslation('offers');
  const deleteOffer = useDeleteOffer();
  const navigate = useNavigate();

  async function handleConfirm() {
    try {
      await deleteOffer.mutateAsync({ id: offerId });
      await navigate({ search: { view: 'mine' }, to: '/offers' });
    } catch {
      toast.error(t('workspace.header.trash.toasts.error'));
    }
  }

  return (
    <ModalDialog
      closeLabel={t('workspace.header.trash.close')}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workspace.header.trash.actions.cancel')}
          </Button>
          <Button
            aria-label={t('workspace.header.trash.actions.confirmAriaLabel')}
            disabled={deleteOffer.isPending}
            type="button"
            variant="destructive"
            onClick={() => void handleConfirm()}
          >
            {t('workspace.header.trash.actions.confirm')}
          </Button>
        </>
      }
      header={<h2 className="text-base font-semibold">{t('workspace.header.trash.title')}</h2>}
      onClose={onClose}
      testId="offer-trash-dialog"
    >
      <p className="text-sm text-muted-foreground">{t('workspace.header.trash.description')}</p>
    </ModalDialog>
  );
}
